"use client";

/**
 * DonatePanel — the last step of Data Share: agree, donate, keep the withdrawal links.
 *
 * Donations go to shared_backend/apps/marketing/api/data_share.py, which stores the redacted copy in the
 * e2 bucket askfinz-data-share. Each donation comes back with a private withdrawal token. We keep only a
 * hash of it, never the visitor's name, email or address, so the link shown here is the ONLY way to
 * withdraw. The panel says so plainly and offers copy and download.
 *
 * The withdrawal link carries the token after `#`, so it is never sent to a server or logged when opened.
 */

import { useState } from "react";
import { Check, Copy, Download, HeartHandshake, Loader2, ShieldCheck, TriangleAlert } from "lucide-react";

export type Donated = { id: string; token: string; title: string; provider: string };
export type DonationState = {
  status: "idle" | "sending" | "done";
  done: Donated[];
  refused: { title: string; reason: string }[];
  error: string;
};

function withdrawLink(d: Donated): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://askfinz.com";
  return `${origin}/data-share/withdraw#${d.id}.${d.token}`;
}

export function DonatePanel({
  chosen,
  total,
  consent,
  setConsent,
  donation,
  busy,
  onDonate,
  onDownload,
}: {
  chosen: number;
  total: number;
  consent: boolean;
  setConsent: (v: boolean) => void;
  donation: DonationState;
  busy: boolean;
  onDonate: () => void;
  onDownload: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const sending = donation.status === "sending";
  const receipt = donation.done
    .map((d) => `${d.title || "Untitled"} (${d.provider})\n${withdrawLink(d)}`)
    .join("\n\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(receipt);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  const saveReceipt = () => {
    const blob = new Blob(
      [`askFinz Data Share — your withdrawal links\nOpen a link to withdraw that conversation. Keep this file private.\n\n${receipt}\n`],
      { type: "text/plain" },
    );
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `askfinz-data-share-withdrawal-links-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  };

  if (donation.status === "done" && donation.done.length) {
    return (
      <div className="mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-400">
          <HeartHandshake className="h-4 w-4" />
          Thank you. {donation.done.length.toLocaleString()} conversation{donation.done.length === 1 ? "" : "s"} donated.
        </div>
        <p className="mt-2 text-[13px] leading-relaxed text-foreground/75">
          Save your withdrawal link{donation.done.length === 1 ? "" : "s"} now. We don&#39;t keep your name, email or address, so
          {donation.done.length === 1 ? " this link is" : " these links are"} the only way to withdraw later. Opening one removes that
          conversation and every copy of it within a day.
        </p>
        <ul className="mt-3 max-h-[200px] space-y-2 overflow-y-auto">
          {donation.done.map((d) => (
            <li key={d.id} className="rounded-lg border hairline bg-background/70 px-3 py-2">
              <div className="truncate text-[12.5px] font-medium">{d.title || "Untitled"}</div>
              <div className="mt-0.5 break-all font-mono text-[11px] text-foreground/60">{withdrawLink(d)}</div>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 rounded-full border hairline-strong bg-background px-3.5 py-1.5 text-[12.5px] font-medium hover:bg-foreground/5">
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy all links"}
          </button>
          <button type="button" onClick={saveReceipt} className="inline-flex items-center gap-1.5 rounded-full border hairline-strong bg-background px-3.5 py-1.5 text-[12.5px] font-medium hover:bg-foreground/5">
            <Download className="h-3.5 w-3.5" />
            Save links as a file
          </button>
        </div>
        {donation.refused.length > 0 && (
          <div className="mt-3 text-[12px] text-foreground/65">
            Not donated: {donation.refused.map((r) => `${r.title || "Untitled"} (${r.reason})`).join("; ")}.
          </div>
        )}
        {donation.error && <p className="mt-2 text-[12px] text-amber-700 dark:text-amber-400">{donation.error} The ones above were saved.</p>}
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-xl border hairline-strong bg-foreground/[0.03] px-4 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <HeartHandshake className="h-4 w-4 text-[hsl(var(--accent))]" />
        <span className="text-sm font-semibold">Donate to help improve askFinz</span>
        <span className="ml-auto text-[12px] text-foreground/55">
          {chosen.toLocaleString()} of {total.toLocaleString()} selected
        </span>
      </div>
      <p className="mt-2 text-[12.5px] leading-relaxed text-foreground/65">
        Only the redacted copy you reviewed is sent, and only conversations that passed the safety check. It is stored privately in
        the EU and used to improve askFinz and its models.
      </p>
      <label className="mt-3 flex items-start gap-2.5 text-[12.5px] leading-relaxed text-foreground/80">
        <input id="ds-consent" type="checkbox" className="mt-1" checked={consent} disabled={sending} onChange={(e) => setConsent(e.target.checked)} />
        <span>
          I agree that askFinz may store the redacted copy of {chosen === 1 ? "this conversation" : "these conversations"} and use it to
          improve askFinz and its models, including Qorra and Search. I can withdraw at any time with the link I get next.{" "}
          <a href="/trust" className="underline underline-offset-2 hover:text-foreground">How we handle data</a>
        </span>
      </label>
      {donation.error && (
        <p className="mt-3 flex items-start gap-2 text-[12.5px] text-amber-700 dark:text-amber-400">
          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {donation.error}
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onDonate}
          disabled={!consent || chosen === 0 || sending || busy}
          className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2 text-[13px] font-medium text-background hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          {sending ? "Donating…" : chosen === 1 ? "Donate this conversation" : `Donate ${chosen.toLocaleString()} conversations`}
        </button>
        <button
          type="button"
          onClick={onDownload}
          disabled={busy || chosen === 0}
          className="inline-flex items-center gap-1.5 rounded-full border hairline-strong px-4 py-2 text-[12.5px] font-medium hover:bg-foreground/5 disabled:opacity-40"
        >
          <Download className="h-3.5 w-3.5" />
          Download what you&#39;d share
        </button>
      </div>
    </div>
  );
}
